import { datadogRum } from '@datadog/browser-rum';
import createError from 'http-errors';
import { ErrorCategory, type EnhancedError } from './exceptions';

interface ErrorDetails {
  message: string;
  statusCode: number;
  cause?: unknown;
  errorType?: string;
  timestamp: string;
  stack?: string;
  originalError?: unknown;
}

const trackErrorInDatadog = (errorDetails: ErrorDetails, originalError: unknown) => {
  try {
    // Add custom context to the error
    const context = {
      statusCode: errorDetails.statusCode,
      errorType: errorDetails.errorType,
      timestamp: errorDetails.timestamp,
      cause: errorDetails.cause,
    };

    // Track the error in Datadog
    datadogRum.addError(originalError, context);

    // Add a custom action to track the error occurrence
    datadogRum.addAction('error_occurred', {
      errorMessage: errorDetails.message,
      ...context,
    });
  } catch (e) {
    // Fallback logging if Datadog tracking fails
    console.error('Failed to track error in Datadog:', e);
  }
};

export const errorHandler = (error: unknown): EnhancedError => {
  console.error('🔴 Error Handler Processing:', {
    error,
    type: error instanceof Error ? error.constructor.name : typeof error,
    stack: error instanceof Error ? error.stack : undefined,
  });

  // If it's already an EnhancedError, return it
  if (error instanceof Error && 'statusCode' in error) {
    return error as EnhancedError;
  }

  // Handle standard Error instances
  if (error instanceof Error) {
    const statusCode = error.name === 'TypeError' ? 400 : 500;
    const category = error.name === 'TypeError' ? ErrorCategory.VALIDATION : ErrorCategory.INTERNAL;
    return createError(statusCode, error.message, {
      cause: error,
      category,
    }) as EnhancedError;
  }

  // Handle fetch/network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return createError(503, 'Service temporarily unavailable. Please try again later.', {
      cause: error,
      category: ErrorCategory.EXTERNAL_SERVICE,
    }) as EnhancedError;
  }

  // Handle API response errors
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    let message = 'An error occurred';
    let category = ErrorCategory.INTERNAL;

    if (status === 401) {
      message = 'Authentication required. Please sign in again.';
      category = ErrorCategory.AUTHENTICATION;
    } else if (status === 403) {
      message = 'You do not have permission to perform this action.';
      category = ErrorCategory.AUTHORIZATION;
    } else if (status === 404) {
      message = 'The requested resource was not found.';
      category = ErrorCategory.NOT_FOUND;
    } else if (status === 409) {
      message = 'This action conflicts with the current state of the resource.';
      category = ErrorCategory.CONFLICT;
    } else if (status >= 500) {
      message = 'An internal server error occurred. Please try again later.';
      category = ErrorCategory.INTERNAL;
    }

    return createError(status, message, {
      cause: error,
      category,
    }) as EnhancedError;
  }

  // Handle unknown errors
  return createError(500, 'An unexpected error occurred', {
    cause: error,
    category: ErrorCategory.INTERNAL,
  }) as EnhancedError;
};
